import React, { useCallback, useEffect, useState, useRef, useId, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { getRegionMeta } from '../utils/region';
// Reusable address autocomplete input.
// - Queries Photon (https://photon.komoot.io/) — free, no API key, fast,
//   autocomplete-optimized OSM search. Biased to the South Sound delivery area.
// - Renders a styled suggestion dropdown with keyboard navigation.
// - When the user picks a suggestion, fires `onSelect(address, lat, lon)`
//   so the parent can skip re-geocoding and act on the coordinates directly.
// - Falls back to the parent's existing debounced geocode if the user types
//   without picking a suggestion.
const PHOTON_URL = 'https://photon.komoot.io/api/';
export type AddressSuggestion = {
  label: string;
  fullAddress: string;
  lat: number;
  lon: number;
};
type Props = {
  value: string;
  onChange: (val: string) => void;
  onSelect?: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  inputClassName?: string;
  containerClassName?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isLoading?: boolean;
  required?: boolean;
  autoComplete?: string;
  inputId?: string;
  inputAriaLabel?: string;
  /** Style for the suggestion dropdown ("dark" matches the Hero glass card,
   * "light" matches light surfaces). */
  variant?: 'dark' | 'light';
  /** Open the dropdown above the input instead of below. Useful for
   * inputs anchored to the bottom of the viewport. */
  dropDirection?: 'down' | 'up';
};
type PhotonFeature = {
  geometry: {
    coordinates: [number, number];
  };
  properties: {
    osm_id?: number;
    name?: string;
    housenumber?: string;
    street?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
    countrycode?: string;
    type?: string;
    osm_value?: string;
  };
};
// OSM internal identifiers leak through Photon when the underlying way
// or node has no real name. They look like "W-12345 Road", "N-678",
// "R-90" — never a real-world address.
const OSM_ID_NAME = /^[WNR]-\d+/i;
function formatSuggestion(f: PhotonFeature): AddressSuggestion | null {
  const p = f.properties;
  if (p.countrycode && p.countrycode !== 'US') return null;
  if (p.state && p.state !== 'Washington') return null;
  // REQUIRE a real street. POIs, lakes, parks, and OSM-id-labeled ways
  // (e.g. "W-1280 Road") are not addresses and just confuse customers
  // typing into a delivery field. If a feature has no street, drop it.
  if (!p.street) return null;
  const street = [p.housenumber, p.street].filter(Boolean).join(' ').trim();
  if (!street) return null;
  // Defense in depth — reject any leftover OSM-id-shaped names that
  // somehow slipped into the street field.
  if (OSM_ID_NAME.test(street)) return null;
  const cityBit = [p.city, p.state ? 'WA' : undefined, p.postcode].filter(Boolean).join(', ');
  const full = [street, cityBit].filter(Boolean).join(', ');
  return {
    label: street,
    fullAddress: full,
    lat: f.geometry.coordinates[1],
    lon: f.geometry.coordinates[0]
  };
}
export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  inputClassName = '',
  containerClassName = '',
  leftIcon,
  rightIcon,
  isLoading = false,
  required,
  autoComplete = 'street-address',
  inputId,
  inputAriaLabel,
  variant = 'dark',
  dropDirection = 'down'
}: Props) {
  const regionCenter = getRegionMeta().center || [47.2073, -122.197];
  const BIAS_LAT = regionCenter[0];
  const BIAS_LON = regionCenter[1];
  const reactId = useId();
  const listboxId = `${inputId || reactId}-listbox`;
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const [isFetching, setIsFetching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Track the value the parent just received from a user pick — used to
  // suppress the next debounced fetch (which would just re-search for the
  // selected address).
  const justSelectedRef = useRef(false);
  // Close on click outside.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setActiveIdx(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  // Debounced Photon query as the user types.
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    const q = value.trim();
    // Don't query until the customer has typed enough to form a real
    // street query. A bare house number ("1280") returns OSM noise —
    // require at least 3 characters AND at least one letter so we know
    // the user has started typing a street name.
    if (q.length < 3 || !/[a-zA-Z]/.test(q)) {
      setSuggestions([]);
      setOpen(false);
      setActiveIdx(-1);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      setIsFetching(true);
      try {
        const url = `${PHOTON_URL}?q=${encodeURIComponent(q)}&lat=${BIAS_LAT}&lon=${BIAS_LON}&limit=6&lang=en`;
        const res = await fetch(url, {
          signal: ctrl.signal
        });
        const data = await res.json();
        const feats: PhotonFeature[] = data?.features || [];
        const mapped: AddressSuggestion[] = [];
        const seen = new Set<string>();
        for (const f of feats) {
          const s = formatSuggestion(f);
          if (!s) continue;
          if (seen.has(s.fullAddress)) continue;
          seen.add(s.fullAddress);
          mapped.push(s);
          if (mapped.length >= 5) break;
        }
        setSuggestions(mapped);
        setOpen(mapped.length > 0);
        setActiveIdx(-1);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setSuggestions([]);
          setOpen(false);
        }
      } finally {
        setIsFetching(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [value]);
  const handleSelect = useCallback((s: AddressSuggestion) => {
    justSelectedRef.current = true;
    onChange(s.fullAddress);
    setSuggestions([]);
    setOpen(false);
    setActiveIdx(-1);
    onSelect?.(s);
  }, [onChange, onSelect]);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => i <= 0 ? suggestions.length - 1 : i - 1);
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && activeIdx < suggestions.length) {
        e.preventDefault();
        handleSelect(suggestions[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
    }
  };
  // Dropdown treatment — sits below the input as its own clean
  // green-bordered pill, mirroring the input's rounded outline so the
  // two read as a matched pair. Green border + green option labels
  // make the list immediately legible as "tappable verified-area
  // addresses".
  const dropdownStyles = variant === 'light' ? 'bg-white border-[#059F40]/50 text-stone-900 shadow-xl' : 'bg-stone-900/95 backdrop-blur-md border-[#22c95c]/45 text-white shadow-xl';
  const itemHover = variant === 'light' ? 'hover:bg-[#059F40]/10' : 'hover:bg-[#22c95c]/10';
  const itemActive = variant === 'light' ? 'bg-[#059F40]/10' : 'bg-[#22c95c]/10';
  const subText = variant === 'light' ? 'text-stone-500' : 'text-white/55';
  const optionLabelColor = variant === 'light' ? 'text-[#059F40]' : 'text-[#22c95c]';
  const isMerged = open && suggestions.length > 0;
  return <div className={`relative ${containerClassName} ${isMerged ? 'address-autocomplete-merged' : ''}`} ref={containerRef} data-id="element-155">
      {/* Input-row wrapper — keeps the absolutely-positioned left and
                 right icons anchored to JUST the input's height. Without this
                 wrapper the icons span the full merged-pill height and end up
                 floating over the suggestion rows when the dropdown is open. */}
      <div className="relative" data-id="element-156">
        {leftIcon && <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10" data-id="element-157">
            {leftIcon}
          </div>}
        <input id={inputId} type="text" value={value} onChange={e => onChange(e.target.value)} onFocus={() => suggestions.length > 0 && setOpen(true)} onKeyDown={handleKeyDown} placeholder={placeholder} autoComplete={autoComplete} required={required} aria-label={inputAriaLabel} aria-autocomplete="list" aria-expanded={open} aria-controls={listboxId} aria-activedescendant={activeIdx >= 0 ? `${listboxId}-opt-${activeIdx}` : undefined} role="combobox" className={inputClassName} data-id="element-158" />
        {(isLoading || isFetching) && <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none z-10" data-id="element-159">
            <Loader2 className="w-4 h-4 text-[#059F40] animate-spin" data-id="element-160" />
          </div>}
        {!isLoading && !isFetching && rightIcon && <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none z-10" data-id="element-161">
            {rightIcon}
          </div>}
      </div>
      <AnimatePresence data-id="element-162">
        {open && suggestions.length > 0 && <motion.ul id={listboxId} role="listbox" initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15
      }} className="relative left-0 right-0 z-50 overflow-hidden" data-id="element-163">
            {suggestions.map((s, i) => {
          const active = i === activeIdx;
          return <li id={`${listboxId}-opt-${i}`} key={`${s.lat},${s.lon},${i}`} role="option" aria-selected={active} onMouseDown={e => {
            // mousedown (not click) so we beat the blur that would
            // otherwise close the dropdown before the click fires.
            e.preventDefault();
            handleSelect(s);
          }} onMouseEnter={() => setActiveIdx(i)} className={`px-4 py-3 text-sm cursor-pointer transition-colors ${active ? itemActive : itemHover} ${variant === 'dark' ? 'border-t border-[#22c95c]/25 first-of-type:border-t-0' : 'border-t border-[#059F40]/25 first-of-type:border-t-0'}`} data-id="element-164">
                  <div className="flex items-start justify-between gap-3" data-id="element-165">
                    <div className="min-w-0 flex-1" data-id="element-166">
                      <div className={`font-normal leading-tight text-sm ${optionLabelColor}`} data-id="element-167">
                        {s.label}
                      </div>
                      <div className={`text-sm mt-0.5 leading-tight font-normal ${subText}`} data-id="element-168">
                        {s.fullAddress.replace(`${s.label}, `, '') || s.fullAddress}
                      </div>
                    </div>
                    {/* Tiny affordance label — teaches first-time users
                     that the suggestion is tappable. Only shows on the
                     currently-highlighted option so it stays quiet. */}
                    {active && <span className={`shrink-0 mt-0.5 text-[10px] uppercase tracking-wider font-bold ${variant === 'dark' ? 'text-[#22c95c]' : 'text-[#059F40]'}`} data-id="element-169">
                        Tap to confirm
                      </span>}
                  </div>
                </li>;
        })}
          </motion.ul>}
      </AnimatePresence>
    </div>;
}